import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import AttorneyLayout from './components/Layout/AttorneyLayout';
import FilingQueue from './components/FilingQueue/FilingQueue';
import ApprovalQueue from './components/ApprovalQueue/ApprovalQueue';
import Clients from './components/Clients';
import ClientMessages from './components/ClientMessages';
import InternalRequests from './components/InternalRequests';
import MarkoTMResponses from './components/MarkoTMResponses';
import Correspondence from './components/Correspondence';
import Deadlines from './components/Deadlines';
import Jurisdictions from './components/Jurisdictions';
import MyTeam from './components/MyTeam';
import { AttorneyProvider } from './context/AttorneyContext';
import { NotificationProvider } from '../context/NotificationContext';
import { useCurrentFirm } from '../hooks/queries/useLawFirms';
import './AttorneyApp.css';

/**
 * Attorney Dashboard Application
 * Main entry point for the attorney portal
 *
 * Access is determined by checking if user's org is registered in law_firms table.
 * This allows both internal (MarkoTM) and external law firm attorneys to access.
 */
function AttorneyApp() {
  const { isLoaded, isSignedIn } = useUser();

  // Check access via law_firms table lookup (TanStack Query)
  const { data: firmData, isLoading: firmLoading, error: firmError } = useCurrentFirm();

  // Derive access from API response
  const hasAttorneyAccess = firmData?.hasAccess === true;
  const isAdmin = firmData?.permissions?.isAdmin === true;

  // Wait for auth and firm data to load
  if (!isLoaded || firmLoading) {
    return (
      <div className="attorney-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect if not signed in
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!hasAttorneyAccess) {
    return (
      <div className="attorney-access-denied">
        <h1>Access Denied</h1>
        <p>
          {firmError
            ? 'Unable to verify access. Please try again later.'
            : 'Your organization is not registered for attorney access. Please contact your administrator.'}
        </p>
        <a href="/dashboard">Return to Dashboard</a>
      </div>
    );
  }

  return (
    <NotificationProvider>
      <AttorneyProvider>
        <AttorneyLayout>
          <Routes>
            <Route index element={<Navigate to="filing-queue" replace />} />
            <Route path="filing-queue" element={<FilingQueue />} />
            <Route path="approval-queue" element={<ApprovalQueue />} />
            <Route path="clients" element={<Clients />} />
            <Route path="client-messages" element={<ClientMessages />} />
            <Route path="internal-requests" element={<InternalRequests />} />
            <Route path="markotm-responses" element={<MarkoTMResponses />} />
            <Route path="correspondence" element={<Correspondence />} />
            <Route path="deadlines" element={<Deadlines />} />
            <Route path="jurisdictions" element={<Jurisdictions />} />
            <Route path="my-team" element={<MyTeam />} />
            <Route path="*" element={<Navigate to="filing-queue" replace />} />
          </Routes>
        </AttorneyLayout>
      </AttorneyProvider>
    </NotificationProvider>
  );
}

export default AttorneyApp;
